import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { X, Clock } from 'lucide-react';
import { format, addDays, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';
import AppointmentService from '../../services/appoinment.service';
import DoctorCalendarService from '../../services/doctor.calendar.service';

const RescheduleAppointmentModal = ({ appointment, onClose, formatDate, formatTime }) => {
  // Stati per la selezione della data e slot
  const [availableDates, setAvailableDates] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState('');
  const [loadingDates, setLoadingDates] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [calendarError, setCalendarError] = useState('');
  const [rescheduleNote, setRescheduleNote] = useState('');
  const [savingReschedule, setSavingReschedule] = useState(false);
  const [rescheduleSuccess, setRescheduleSuccess] = useState(false);

  // Carica le date disponibili all'apertura della modale
  useEffect(() => {
    fetchAvailableDates(appointment.therapist_id);
  }, [appointment]);


  // Quando viene selezionata una data, carica gli slot disponibili per quella data
  useEffect(() => {
    console.log('Appointment object:', appointment);

    if (selectedDate && appointment) {
      fetchAvailableSlots(selectedDate, appointment.therapist_id);
    }
  }, [selectedDate, appointment]);

  // Funzione per caricare le date disponibili
  const fetchAvailableDates = async (therapistId) => {
    setLoadingDates(true);
    setCalendarError('');
    try {
      // Ottieni il primo e l'ultimo giorno del periodo di interesse (oggi + 30 giorni)
      const today = new Date();
      const tomorrow = addDays(today, 1); // Inizia dal giorno dopo
      const startDate = format(tomorrow, 'yyyy-MM-dd');
      const endDate = format(addDays(today, 30), 'yyyy-MM-dd'); // Prossimi 30 giorni

      const response = await DoctorCalendarService.getAvailabilities(
        appointment.therapist_user_id,
        startDate,
        endDate
      );

      // Estrai date uniche dagli slot disponibili
      const uniqueDates = Array.from(new Set(response.available_slots.map(slot => slot.date)));
      const sortedDates = uniqueDates.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

      // Converti le stringhe di date in oggetti Date
      const validDates = sortedDates.map(date => parseISO(date));

      setAvailableDates(validDates);
    } catch (err) {
      console.error('Errore nel caricamento delle date disponibili:', err);
      setCalendarError('Impossibile caricare le date disponibili. Riprova più tardi.');
    } finally {
      setLoadingDates(false);
    }
  };

  // Funzione per caricare gli slot disponibili per una data
  const fetchAvailableSlots = async (date, therapistId) => {
    setLoadingSlots(true);
    setCalendarError('');
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      const nextDayStr = format(addDays(date, 1), 'yyyy-MM-dd');

      const response = await DoctorCalendarService.getAvailabilities(
        appointment.therapist_user_id,
        dateStr,
        nextDayStr
      );

      // Filtra solo gli slot per la data selezionata
      const slotsForDate = response.available_slots.filter(slot => slot.date === dateStr);

      // Ordina gli slot per orario
      const sortedSlots = slotsForDate.sort((a, b) => {
        return a.start.localeCompare(b.start);
      });

      setAvailableSlots(sortedSlots);
    } catch (err) {
      console.error('Errore nel caricamento degli slot disponibili:', err);
      setCalendarError('Impossibile caricare gli orari disponibili. Riprova più tardi.');
    } finally {
      setLoadingSlots(false);
    }
  };

  // Funzione per formattare la data in modo leggibile
  const formatCalendarDate = (date) => {
    return format(date, 'EEEE, d MMM', { locale: it });
  };

  // Funzione per verificare se due date sono lo stesso giorno
  const isSameDay = (date1, date2) => {
    return date1 && date2 && format(date1, 'yyyy-MM-dd') === format(date2, 'yyyy-MM-dd');
  };

  // Funzione per salvare l'appuntamento suggerito
  const handleSaveReschedule = async () => {
    if (!selectedDate || !selectedSlot || !appointment) {
      alert('Seleziona data e ora per il nuovo appuntamento');
      return;
    }

    try {
      setSavingReschedule(true);
      setRescheduleSuccess(false);

      // Prepara i dati per l'API
      const appointmentData = {
        patient_id: appointment.patient_id, // Passa l'ID del paziente
        therapist_id: appointment.therapist_user_id, // ID utente del terapeuta
        service_id: appointment.service_id,
        service_name: appointment.service_name,
        date: format(selectedDate, 'yyyy-MM-dd'),
        time: selectedSlot,
        price: appointment.price,
        total_price: appointment.total_price || appointment.price,
        notes: rescheduleNote
      };

      // Chiama il metodo createSuggested
      await AppointmentService.createSuggested(appointmentData);

      // Mostra messaggio di successo
      setRescheduleSuccess(true);

      // Nascondi il messaggio di successo dopo 3 secondi e chiudi il modale
      setTimeout(() => {
        setRescheduleSuccess(false);
        onClose();
      }, 3000);

    } catch (err) {
      console.error('Errore nel salvataggio dell\'appuntamento suggerito:', err);
      alert('Non è stato possibile salvare l\'appuntamento suggerito. Riprova più tardi.');
    } finally {
      setSavingReschedule(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-screen overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold">
            Rifissa appuntamento
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="bg-purple-50 p-4 rounded-lg mb-6">
          <h4 className="font-medium text-purple-800 mb-2">Appuntamento attuale</h4>
          <p className="text-gray-700 mb-1">
            <span className="font-medium">Paziente:</span> {appointment.patient_name || `Paziente #${appointment.patient_id}`}
          </p>
          <p className="text-gray-700 mb-1">
            <span className="font-medium">Data:</span> {formatDate(appointment.date)}
          </p>
          <p className="text-gray-700 mb-1">
            <span className="font-medium">Ora:</span> {formatTime(appointment.time)}
          </p>
          <p className="text-gray-700">
            <span className="font-medium">Servizio:</span> {appointment.service_name}
          </p>
        </div>

        <div className="mb-6">
          <h4 className="font-medium text-gray-700 mb-4">Seleziona data e ora per il nuovo appuntamento</h4>

          {/* Selettore di data */}
          <div className="mb-6">
            <h5 className="text-sm font-medium text-gray-600 mb-3">Data disponibile</h5>

            {loadingDates ? (
              <div className="text-center py-4">
                <svg className="animate-spin h-6 w-6 mx-auto text-purple-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <p className="mt-2 text-gray-600">Caricamento date disponibili...</p>
              </div>
            ) : availableDates.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 max-h-48 overflow-y-auto">
                {availableDates.slice(0, 20).map((date, index) => (
                  <div
                    key={index}
                    onClick={() => {
                      setSelectedDate(date);
                      setSelectedSlot(''); // Reset selected slot when date changes
                    }}
                    className={`p-3 rounded-md cursor-pointer border transition-colors ${isSameDay(date, selectedDate)
                        ? 'bg-purple-100 border-purple-300 text-purple-800'
                        : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                      }`}
                  >
                    {formatCalendarDate(date)}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center py-4 text-gray-600">
                {calendarError || "Nessuna data disponibile nei prossimi giorni"}
              </p>
            )}
          </div>

          {/* Selettore di orario */}
          {selectedDate && (
            <div className="mb-6">
              <h5 className="text-sm font-medium text-gray-600 mb-3">Orario disponibile</h5>

              {loadingSlots ? (
                <div className="text-center py-4">
                  <svg className="animate-spin h-6 w-6 mx-auto text-purple-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <p className="mt-2 text-gray-600">Caricamento orari disponibili...</p>
                </div>
              ) : availableSlots.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {availableSlots.map(slot => (
                    <button
                      key={`${slot.date}-${slot.start}`}
                      onClick={() => setSelectedSlot(slot.start)}
                      className={`py-2 px-4 rounded transition-colors ${selectedSlot === slot.start
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-100 hover:bg-gray-200'
                        }`}
                    >
                      {slot.start}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-center py-4 text-gray-600">
                  {calendarError || "Nessun orario disponibile per la data selezionata"}
                </p>
              )}
            </div>
          )}

          {/* Riepilogo selezione */}
          {selectedDate && selectedSlot && (
            <div className="bg-green-50 p-4 rounded-lg border border-green-100 mb-6">
              <h5 className="font-medium text-green-800 mb-2">Nuovo appuntamento</h5>
              <p className="text-gray-700 mb-1">
                <span className="font-medium">Data:</span> {formatCalendarDate(selectedDate)}
              </p>
              <p className="text-gray-700 mb-1">
                <span className="font-medium">Ora:</span> {selectedSlot}
              </p>
              <p className="text-gray-700">
                <span className="font-medium">Servizio:</span> {appointment.service_name}
              </p>
            </div>
          )}
        </div>

        <div className="mb-6">
          <label htmlFor="reschedule-note" className="block text-sm font-medium text-gray-700 mb-2">
            Nota per il paziente (opzionale)
          </label>
          <textarea
            id="reschedule-note"
            rows={3}
            className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
            placeholder="Aggiungi un messaggio per il paziente riguardo al motivo della richiesta di rifissare l'appuntamento..."
            value={rescheduleNote}
            onChange={(e) => setRescheduleNote(e.target.value)}
          ></textarea>
        </div>

        {/* Messaggio di successo */}
        {rescheduleSuccess && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4 flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <p>Appuntamento suggerito salvato correttamente!</p>
          </div>
        )}

        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
          >
            Annulla
          </button>
          <button
            onClick={handleSaveReschedule}
            disabled={savingReschedule || !selectedDate || !selectedSlot}
            className={`px-4 py-2 rounded flex items-center ${savingReschedule || !selectedDate || !selectedSlot
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-purple-600 text-white hover:bg-purple-700'
              }`}
          >
            {savingReschedule ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Salvataggio...
              </>
            ) : (
              'Salva appuntamento suggerito'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

RescheduleAppointmentModal.propTypes = {
  appointment: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    date: PropTypes.string.isRequired,
    time: PropTypes.string.isRequired,
    service_name: PropTypes.string,
    service_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    status: PropTypes.string.isRequired,
    patient_name: PropTypes.string,
    patient_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    patient_user_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    therapist_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    therapist_user_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired, // Aggiunta qui
    price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    total_price: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  formatDate: PropTypes.func.isRequired,
  formatTime: PropTypes.func.isRequired
};

export default RescheduleAppointmentModal;